import { ComponentType } from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NotificationPopupComponent } from '@app/shared/components/notification-popup/notification-popup.component';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SnackbarManager {
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  /**
   * Display a snackbar message indefinitely with an optional dismissal action.
   * @param message Message to display.
   * @param action Text content of the action button.
   * @return Reference to the snackbar, to listen for user action.
   */
  public showInfiniteSnackbar(message: string, action?: string): MatSnackBarRef<SimpleSnackBar> {
    return this.snackBar.open(message, action ?? this.translate.instant('BUTTON.CLOSE'), {
      duration: 0,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  /**
   * Display a snackbar message for 3 seconds.
   * @param message Message to display.
   * @return Reference to the snackbar.
   */
  public showActionSnackbar(message: string): MatSnackBarRef<SimpleSnackBar> {
    return this.snackBar.open(message, null, {
      duration: 3000,
      panelClass: 'snackbar-no-action',
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  /**
   * Display a snackbar based on a custom component indefinitely.
   * @param renderComponent Component to render within the snackbar.
   * @return Reference to the snackbar.
   */
  public showGenericSnackbar(renderComponent: ComponentType<any>): MatSnackBarRef<any> {
    return this.snackBar.openFromComponent(renderComponent, {
      duration: 0,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  public showNotificationSnackbar(message: string): MatSnackBarRef<NotificationPopupComponent> {
    return this.snackBar.openFromComponent(NotificationPopupComponent, {
      duration: 0,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'notification-snackbar',
      data: {
        newFeatureMessage: message
      }
    });
  }

  /**
   * Dismiss any currently displayed snackbar.
   */
  public closeSnackbar(): void {
    this.snackBar.dismiss();
  }
}
